import type { ChatLocalMesage } from '../types'
import { MESSAGE_URL_REGEX, type QuotedPreviewModel } from './messages'
import { createMentionRegex } from './mentions'

// @spec FR-0077/BR-0029@v1 — icons that decorate the quoted preview body.
export const QUOTED_PREVIEW_ICONS = {
  voice: 'microphone-01',
  document: 'attachment-01',
  poll: 'menu-03',
} as const satisfies Partial<Record<QuotedPreviewModel['kind'], string>>

// @spec FR-0077/AC-5@v1 — bubble + composer memo equality.
export function quotedMessageMemoEquality(
  a: ChatLocalMesage | null | undefined,
  b: ChatLocalMesage | null | undefined,
): boolean {
  return (
    a?.id === b?.id
    && a?.type === b?.type
    && a?.deleted_at === b?.deleted_at
    && a?.text === b?.text
  )
}

// @spec BR-0029@v1 — body line for image-album quoted previews.
export function getQuotedImageAlbumLabel(count: number): string {
  return `${count} Photos`
}

// @spec FR-0077/AC-12@v1 — assistive label for the quoted preview tile.
export function buildQuotedPreviewA11yLabel(
  preview: Exclude<QuotedPreviewModel, { kind: 'deleted' }>,
): string {
  const sender = preview.isCurrentUser ? 'You' : preview.senderName
  switch (preview.kind) {
    case 'text':     return `Quoted message from ${sender}: ${preview.text}`
    case 'image':    return preview.count > 1
      ? `Quoted ${preview.count} photos from ${sender}`
      : `Quoted image from ${sender}`
    case 'gif':      return `Quoted GIF from ${sender}`
    case 'voice':    return `Quoted voice message from ${sender}, ${preview.durationLabel}`
    case 'poll':     return `Quoted poll from ${sender}: ${preview.title}`
    case 'document': return `Quoted document from ${sender}: ${preview.filename}${preview.extension}`
  }
}

// @spec FR-0077/AC-7@v1 + BR-0031@v1 — resolve the live-or-snapshot quoted message for display.
export function resolveQuotedForDisplay(
  snapshot: ChatLocalMesage | null | undefined,
  liveQuoted: ChatLocalMesage | null | undefined,
  wasDeletedThisSession: boolean,
): ChatLocalMesage | null {
  if (!snapshot) return null
  const resolved = (liveQuoted ?? snapshot) as ChatLocalMesage
  if (wasDeletedThisSession) {
    return { ...resolved, type: 'deleted' } as ChatLocalMesage
  }
  return resolved
}

type MentionedUserLike = { id?: string; name?: string }

// @spec FR-0077@v1 — patterns tinted inside the quoted preview body.
export function getQuotedPreviewMatchers(mentionedUsers?: MentionedUserLike[]) {
  return {
    mention: createMentionRegex(mentionedUsers ?? []),
    link: MESSAGE_URL_REGEX,
  }
}

export type QuotedTextPart = { kind: 'text' | 'link' | 'mention'; value: string }

type MatchHit = { kind: 'link' | 'mention'; start: number; end: number; value: string }

// Pure splitter: walks `text` against the URL + mention regexes from
// `getQuotedPreviewMatchers`, drops overlapping matches (URL wins on ties
// because it's collected first), and returns alternating text / link /
// mention runs. Used by the web QuotedTextWithLinks renderer today; mobile
// uses ParsedText which does its own segmentation, but this stays generic
// enough to drive any consumer that wants the parts as data.
export function splitQuotedText(text: string, mentionedUsers?: MentionedUserLike[]): QuotedTextPart[] {
  const { mention, link } = getQuotedPreviewMatchers(mentionedUsers)
  const matches: MatchHit[] = []
  collectQuotedMatches(text, link, 'link', matches)
  collectQuotedMatches(text, mention, 'mention', matches)

  matches.sort((a, b) => a.start - b.start)
  const filtered: MatchHit[] = []
  let lastEnd = -1
  for (const m of matches) {
    if (m.start >= lastEnd) {
      filtered.push(m)
      lastEnd = m.end
    }
  }

  const parts: QuotedTextPart[] = []
  let cursor = 0
  for (const m of filtered) {
    if (m.start > cursor) parts.push({ kind: 'text', value: text.slice(cursor, m.start) })
    parts.push({ kind: m.kind, value: m.value })
    cursor = m.end
  }
  if (cursor < text.length) parts.push({ kind: 'text', value: text.slice(cursor) })
  return parts.length > 0 ? parts : [{ kind: 'text', value: text }]
}

function collectQuotedMatches(text: string, source: RegExp, kind: MatchHit['kind'], out: MatchHit[]) {
  const regex = new RegExp(source.source, source.flags)
  let match: RegExpExecArray | null
  while ((match = regex.exec(text)) !== null) {
    if (match[0].length === 0) {
      regex.lastIndex += 1
      continue
    }
    out.push({ kind, start: match.index, end: match.index + match[0].length, value: match[0] })
  }
}
